import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { useForm } from "src/api/hooks/useForm";
import useGroups from "src/api/hooks/useGroups";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { lang } from "src/language/Strings";
import { objectTypes } from "src/consts/objectTypes";
import FormFieldsWrapper from "src/components/Forms/FormFieldsWrapper";

type PeriodType = "date" | "month" | "week";

interface ReportFormData {
  groupId?: number;
  customer?: string;
  objectType?: keyof typeof objectTypes;
  periodType?: PeriodType;
  periodDate?: Date;
  periodMonth?: string; // Format: "YYYY-MM"
  periodWeek?: number;
  periodYear?: number; // For week number
  search?: string;
}

interface ReportPayload {
  groupId?: number;
  customer?: string;
  objectType?: keyof typeof objectTypes;
  periodType?: PeriodType;
  periodDate?: Date;
  periodMonth?: string;
  periodWeek?: number;
  periodYear?: number;
  search?: string;
  deviationIds: number[];
}

/**
 * Get ISO week number (Swedish week standard)
 * ISO 8601 week: Week 1 is the week with the year's first Thursday
 */
function getISOWeekNumber(date: Date): { week: number; year: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { week, year: d.getUTCFullYear() };
}

/**
 * Get the date range for a given ISO week number and year
 */
function getWeekDateRange(week: number, year: number): { start: Date; end: Date } {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  const start = new Date(ISOweekStart);
  const end = new Date(ISOweekStart);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function ReportPage() {
  useChangeTitle(lang.report);
  const { companyId } = useParams();
  const { data: groups } = useGroups();
  const { data: deviations } = useProjectVariations();

  const { Form, formData: formState, setFormData: setFormState } = useForm<ReportFormData>({
    groupId: undefined,
    customer: "",
    objectType: undefined,
    periodType: "date",
    periodDate: undefined,
    periodMonth: "",
    periodWeek: undefined,
    periodYear: new Date().getFullYear(),
    search: "",
  });

  // Get unique customers from deviations
  const uniqueCustomers = useMemo(() => {
    if (!deviations) return [];
    const customers = deviations
      .map((d) => d.customer)
      .filter((c): c is string => !!c && c !== "null");
    return Array.from(new Set(customers));
  }, [deviations]);

  // Filter deviations based on form data
  const filteredDeviationIds = useMemo(() => {
    if (!deviations) return [];

    let filtered = [...deviations];

    // Filter by groupId
    if (formState.groupId !== undefined && formState.groupId !== null && formState.groupId !== 0) {
      filtered = filtered.filter((d) => d.groupId === formState.groupId);
    }

    // Filter by customer
    if (formState.customer) {
      filtered = filtered.filter(
        (d) => d.customer?.toLowerCase().includes(formState.customer!.toLowerCase())
      );
    }

    // Filter by objectType
    if (formState.objectType) {
      filtered = filtered.filter((d) => d.objectType === formState.objectType);
    }

    // Filter by period (based on updatedAt)
    if (formState.periodType) {
      filtered = filtered.filter((d) => {
        if (!d.updatedAt) return false;
        const updatedDate = new Date(d.updatedAt);

        if (formState.periodType === "date" && formState.periodDate) {
          const filterDate = new Date(formState.periodDate);
          return (
            updatedDate.getFullYear() === filterDate.getFullYear() &&
            updatedDate.getMonth() === filterDate.getMonth() &&
            updatedDate.getDate() === filterDate.getDate()
          );
        }

        if (formState.periodType === "month" && formState.periodMonth) {
          const [year, month] = formState.periodMonth.split("-").map(Number);
          return (
            updatedDate.getFullYear() === year && updatedDate.getMonth() === month - 1
          );
        }

        if (
          formState.periodType === "week" &&
          formState.periodWeek !== undefined &&
          formState.periodYear !== undefined
        ) {
          const { week: deviationWeek, year: deviationYear } = getISOWeekNumber(updatedDate);
          return (
            deviationWeek === formState.periodWeek &&
            deviationYear === formState.periodYear
          );
        }

        return true;
      });
    }

    // Filter by search term
    if (formState.search) {
      const searchLower = formState.search.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.title?.toLowerCase().includes(searchLower) ||
          d.description?.toLowerCase().includes(searchLower) ||
          d.customer?.toLowerCase().includes(searchLower) ||
          d.information?.toLowerCase().includes(searchLower)
      );
    }

    return filtered.map((d) => d.id);
  }, [deviations, formState]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const payload: ReportPayload = {
        groupId: formState.groupId,
        customer: formState.customer || undefined,
        objectType: formState.objectType,
        periodType: formState.periodType,
        periodDate: formState.periodDate,
        periodMonth: formState.periodMonth || undefined,
        periodWeek: formState.periodWeek,
        periodYear: formState.periodYear,
        search: formState.search || undefined,
        deviationIds: filteredDeviationIds,
      };

      console.log("Report Payload:", payload);
      // TODO: Implement actual export functionality
    },
    [formState, filteredDeviationIds]
  );

  const currentYear = new Date().getFullYear();
  const weekOptions = useMemo(() => {
    const weeks: number[] = [];
    for (let week = 1; week <= 53; week++) {
      weeks.push(week);
    }
    return weeks;
  }, []);

  return (
    <main className="flex flex-grow text-gray-900 flex-col">
      <div className={"w-full justify-center flex"}>
        <Form
          onSubmit={handleFormSubmit}
          className="w-[80%] max-w-[80%] relative z-0"
          autoComplete="on"
        >
          <FormFieldsWrapper label={lang.group}>
            <Form.Select
              name="groupId"
              id="groupId"
              className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
            >
              <Form.Select.Option value="">Select...</Form.Select.Option>
              {groups?.map((group) => (
                <Form.Select.Option key={group.id} value={group.id}>
                  {group.name}
                </Form.Select.Option>
              ))}
            </Form.Select>
          </FormFieldsWrapper>

          <FormFieldsWrapper label={lang.customer}>
            <datalist id="customer-list" className="hidden">
              {uniqueCustomers.map((customer, index) => (
                <option key={index} value={customer} />
              ))}
            </datalist>
            <Form.Input
              name="customer"
              id="customer"
              type="text"
              list="customer-list"
              autoComplete="off"
              className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
            />
          </FormFieldsWrapper>

          <FormFieldsWrapper label={lang.objectType}>
            <Form.Select
              name="objectType"
              id="objectType"
              className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
            >
              <Form.Select.Option value="">Select...</Form.Select.Option>
              <Form.Select.Option value={objectTypes.Inspection}>
                {lang.inspection}
              </Form.Select.Option>
              <Form.Select.Option value={objectTypes.Deviation}>
                {lang.deviation}
              </Form.Select.Option>
              <Form.Select.Option value={objectTypes.InspectionItem}>
                {lang.item}
              </Form.Select.Option>
            </Form.Select>
          </FormFieldsWrapper>

          <FormFieldsWrapper label="Period Type">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="periodType"
                  value="date"
                  checked={formState.periodType === "date"}
                  onChange={(e) => {
                    setFormState((prev) => ({
                      ...prev,
                      periodType: e.target.value as PeriodType,
                    }));
                  }}
                />
                Date
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="periodType"
                  value="month"
                  checked={formState.periodType === "month"}
                  onChange={(e) => {
                    setFormState((prev) => ({
                      ...prev,
                      periodType: e.target.value as PeriodType,
                    }));
                  }}
                />
                Month
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="periodType"
                  value="week"
                  checked={formState.periodType === "week"}
                  onChange={(e) => {
                    setFormState((prev) => ({
                      ...prev,
                      periodType: e.target.value as PeriodType,
                    }));
                  }}
                />
                Week Number (Swedish weeks)
              </label>
            </div>
          </FormFieldsWrapper>

          {formState.periodType === "date" && (
            <FormFieldsWrapper label="Date">
              <Form.DateInput
                name="periodDate"
                id="periodDate"
                defaultValue={undefined}
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
              />
            </FormFieldsWrapper>
          )}

          {formState.periodType === "month" && (
            <FormFieldsWrapper label="Month">
              <Form.Input
                name="periodMonth"
                id="periodMonth"
                type="month"
                className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
              />
            </FormFieldsWrapper>
          )}

          {formState.periodType === "week" && (
            <>
              <FormFieldsWrapper label="Year">
                <Form.Input
                  name="periodYear"
                  id="periodYear"
                  type="number"
                  min={2000}
                  max={2100}
                  defaultValue={currentYear}
                  className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
                />
              </FormFieldsWrapper>
              <FormFieldsWrapper label="Week Number">
                <Form.Select
                  name="periodWeek"
                  id="periodWeek"
                  className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
                >
                  <Form.Select.Option value="">Select...</Form.Select.Option>
                  {weekOptions.map((week) => (
                    <Form.Select.Option key={week} value={week}>
                      Week {week}
                    </Form.Select.Option>
                  ))}
                </Form.Select>
              </FormFieldsWrapper>
            </>
          )}

          <FormFieldsWrapper label={lang.search}>
            <Form.Input
              name="search"
              id="search"
              type="text"
              autoComplete="off"
              className="border-2 border-Grey9 rounded-lg pt-2 pb-2"
              placeholder={lang.search}
            />
          </FormFieldsWrapper>

          <div className="flex flex-col items-center mt-4">
            <button
              type="submit"
              className="p-2 bg-[#1F4652] text-White rounded-lg disabled:bg-[#1F4652]/50 disabled:opacity-50"
            >
              Export Report
            </button>
            {filteredDeviationIds.length > 0 && (
              <small className="text-Grey7 mt-2">
                {filteredDeviationIds.length} {lang.deviations || "deviations"} found
              </small>
            )}
          </div>
        </Form>
      </div>
    </main>
  );
}
